import { createFocusTrap, FocusTrap } from 'focus-trap';
import { nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { query } from 'lit/decorators/query.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, literal } from 'lit/static-html.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCancelEvent } from '../../events/blocks-cancel';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import { KEYS } from '../../util/event-keys';
import { lockBodyScrolling, unlockBodyScrolling } from '../../util/scroll';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../icon/icons/arrow-triangular-left';
import '../icon/icons/arrow-triangular-right';
import '../icon/icons/close';
import type { DrawerFooterAlignment, DrawerPosition, DrawerSide, DrawerSize } from './drawer.model';
import styles from './drawer.styles';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';

/**
 * @slot header - The header content
 * @slot actions - Actions slot to be displayed.
 * @slot default - The default component content
 * @slot footer - The footer content
 * @fires bl-drawer-open - emitted when drawer is opened
 * @fires bl-drawer-close - emitted when drawer is closed
 * @fires bl-drawer-cancel - emitted before emitting close event so that user can prevent closing drawer
 * @csspart base - The component's base wrapper.
 * @csspart panel - The component's panel wrapper.
 * @csspart hide-button - The component's hide-button wrapper.
 * @csspart button-after-icon - The component's hide button after slot icon.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart header-content - The component's header content.
 * @csspart part-header - The component's header slot.
 * @csspart part-actions - The component's actions slot.
 * @csspart close-button - The component's close-button.
 * @csspart close-button-after-icon - The component's close-button after slot icon.
 * @csspart bottom-wrapper - The component's wrapper for body and footer.
 * @csspart body - The component's body wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksDrawer extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Close button text
   */
  @property({ type: String })
  closeButtonText = 'Close';

  /**
   * Size variant
   * <cat-text-passage size="lg">
   * <ul>
   * <li>**lg** increases the drawer size</li>
   * <li>**sm** decreases the drawer size</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: DrawerSize = 'default';

  /**
   * position variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>position **right** as the default position</li>
   * <li>**left** - position the drawer to the left of the viewport</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  position: DrawerPosition = 'right';

  /**
   * width of drawer panel
   *
   */
  @property({ type: String })
  width: string | undefined;

  /**
   * Fade/ghost back the page background content
   */
  @property({ type: Boolean })
  hasBackdrop = false;

  /**
   * Hide button left or right position
   */
  @property({ type: String })
  hideButton: DrawerSide;

  /**
   * Button text when the drawer is expanded
   */
  @property({ type: String })
  hideButtonText = 'Hide';

  /**
   * Button text when the drawer is collapsed to the side
   */
  @property({ type: String })
  showButtonText = 'Show';

  /**
   * Active state on the drawer
   * aria-labelledby attribute
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   * Alignment for elements within footer
   * Default is left aligned
   */
  @property({ type: String })
  footerAlignment: DrawerFooterAlignment;

  /**
   *  Append an active state on the button
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   *  keep the content of drawer in the DOM when drawer is closed
   */
  @property({ type: Boolean })
  preserveContent = true;

  /**
   *  Disable to close the drawer on outside click
   */
  @property({ type: Boolean })
  disableBackdropClick = false;

  /**
   * A boolean property to specify whether the close button should be hidden or displayed.
   */
  @property({ type: Boolean }) hideCloseButton = false;

  /**
   * @default true , controls whether focus should return to the previously focused element before the drawer opens
   */
  @property({ type: Boolean }) returnFocusOnDeactivate = true;

  /**
   * Disabling drawer close on escape keydown
   */
  @property({ type: Boolean })
  disableEscClick = false;

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * Enable an option to have drawer open from bottom in mobile
   */
  @property({ type: String })
  orientation: 'side' | 'bottom' = 'side';

  /**
   * Set max Height for orientation bottom drawer
   */
  @property({ type: String })
  verticalMaxHeight: 'large' | 'default' = 'default';

  /**
   * Mobile Breakpoint, Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Mobile Variant check for orientation property.
   */
  @state() private isMobile = false;

  /**
   * Set to check if Drawer is Open by clicking on HideButton
   */
  private isDrawerExpandedFromHideButton: boolean | undefined;

  /**Getter for hide button condition to re-use  */
  private get isButtonHidden(): boolean {
    return this.hideButton === 'left' || this.hideButton === 'right';
  }

  /**
   * Query the drawer hide button
   */
  @query('.cat-c-drawer__hide-button')
  private _catDrawerHideButton: HTMLElement | undefined;

  @query('dialog') private dialogElement: HTMLDialogElement | undefined;

  /**
   * Query the drawer close button
   */
  @query('.cat-c-drawer__close-button')
  private _catDrawerCloseButton: HTMLElement | undefined;

  private _focusTrap?: FocusTrap;

  private previouslyActiveElement?: Element | null;

  private wasActive = false;

  protected slots = new SlotController(this, null, 'header', 'actions', 'footer');

  private get hasHeaderSlot(): boolean {
    return this.slots.hasSlotted('header');
  }
  private get hasActionsSlot(): boolean {
    return this.slots.hasSlotted('actions');
  }

  /**
   * Connected callback lifecycle
   * 1) Add event listeners
   * 2)Pause/Unpause focustrap on contextual menu open and close
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    this.addEventListener('bl-contextual-menu-open', () => {
      this._focusTrap?.pause(); /* 2 */
    });

    this.addEventListener('bl-contextual-menu-close', () => {
      this._focusTrap?.unpause(); /* 2 */
    });
    this.addEventListener('bl-dropdown-open', this.pauseFocusTrap, true);
    this.addEventListener('bl-dropdown-close', this.unPauseFocusTrap, true);
    window.addEventListener('resize', this.setMobile);
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    this._focusTrap?.deactivate(); /**To deactivate focus trap */
    this.removeEventListener('bl-dropdown-open', this.pauseFocusTrap, true);
    this.removeEventListener('bl-dropdown-close', this.unPauseFocusTrap, true);
    window.removeEventListener('resize', this.setMobile);
    unlockBodyScrolling(this); /** In case of conditional rendering used to unlock body scroll */
  }

  /**
   *  Pause the focustrap
   */
  private pauseFocusTrap() {
    this._focusTrap?.pause();
  }

  /**
   *  Unpause the focustrap
   */
  private unPauseFocusTrap() {
    this._focusTrap?.unpause();
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the nav is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) If the panel is active and the backdrop click is enabled and we've clicked outside of the panel then it should
   * be closed.
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    /* 2 */
    if (!this.isActive) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine panel context during click handler');
    }

    const eventTarget = event.target as HTMLElement;
    /* 4 */
    if (this.isActive && eventTarget instanceof HTMLDialogElement && !this.disableBackdropClick) {
      this.toggle();
    }
  };

  /**
   *  When the trigger element is clicked and the modal is about to open, blur the trigger element to remove focus from it
   * provided there was an already activeElement and returnFocusOnDeactivate is false
   */
  private blurTriggerElement() {
    if (this.previouslyActiveElement && !this.returnFocusOnDeactivate) {
      (this.previouslyActiveElement as HTMLElement).blur();
    }
  }

  updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isActive') && this.isActive && this.width) {
      const drawerPanel = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-drawer__panel');

      if (drawerPanel) {
        drawerPanel.style.width = this.width;
      }
    }

    if (_changedProperties.has('isActive')) {
      if ((_changedProperties.get('isActive') === false || _changedProperties.get('isActive') === undefined) && !this.previouslyActiveElement) {
        this.previouslyActiveElement = document.activeElement;
      }

      if (!this.isInline && this.isActive) {
        this.blurTriggerElement();
        this.dialogElement?.showModal();
      } else {
        this.dialogElement?.close();
      }

      this.wasActive = _changedProperties.get('isActive') === undefined ? false : true;
      this.toggleFocusTrap();
    }
    if (_changedProperties.has('mobileBreakPoint')) {
      this.setMobile();
    }
  }

  private setAutoFocus() {
    const autoFocusTarget = this.querySelector('[autoFocus]');
    if (autoFocusTarget) {
      (autoFocusTarget as HTMLElement).focus();
    } else if (this._catDrawerCloseButton) {
      this._catDrawerCloseButton.focus();
    }
  }

  /**
   * Set drawer active state
   * 1) If the drawer is active, call the function to lock body scroll.
   * 2) If the drawer is not active, call the function to unlock body scroll.
   */
  private toggleFocusTrap() {
    const body = document.querySelector('body');
    if (this.isActive === true) {
      /* 1 */
      if (body) {
        lockBodyScrolling(this, 'stable');
      }

      setTimeout(() => {
        const drawerPanel = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-drawer__panel');
        if (drawerPanel) {
          this.setAutoFocus();
          /** calling function to create focus trap */
          this._focusTrap = createFocusTrap(drawerPanel, {
            onActivate: () => drawerPanel.classList.add('is-active'),
            onDeactivate: () => drawerPanel.classList.remove('is-active'),
            initialFocus: false,
            tabbableOptions: { getShadowRoot: true },
            returnFocusOnDeactivate: this.returnFocusOnDeactivate,
            allowOutsideClick: true,
            escapeDeactivates: !this.disableEscClick
          });

          /**To activate focus trap */
          this._focusTrap.activate();
        }
      }, 200);
    } else {
      if (body && this.wasActive) {
        unlockBodyScrolling(this);
      }
      /* 2 */
      setTimeout(() => {
        if (this._catDrawerHideButton && this.isDrawerExpandedFromHideButton) {
          this._catDrawerHideButton.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-button')?.focus();
          this.isDrawerExpandedFromHideButton = false;
        } else {
          if (this.returnFocusOnDeactivate) {
            (this.previouslyActiveElement as HTMLElement)?.focus();
            this.previouslyActiveElement = null;
          }
        }
      }, 100);
      /**To deactivate focus trap */
      this._focusTrap?.deactivate();
    }
  }

  /**
   * Handle on keydown
   * 1) If the panel is open and escape is keyed and if `disableEscClick` is false, close the menu and return focus to the trigger button
   */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (this.isActive === true && e.key === KEYS.ESCAPE && !this.disableEscClick) {
      this.toggle();
    }
    if (this.isActive === true && e.key === KEYS.ESCAPE && this.disableEscClick) {
      e.preventDefault();
    }
  };

  /**
   * Toggle drawer
   * @fires open/close - Fires when we open/close drawer by clicking close icon
   * 1) Toggle the active state between true and false
   */
  toggle(): void {
    if (this.isActive) {
      if (this.emitCancelEvent()) {
        return;
      }
    }
    this.isActive = !this.isActive; /* 1 */
    if (this.isActive === true) {
      this.dispatch({ eventName: 'bl-drawer-open', detailObj: { isActive: this.isActive } }) as BlocksOpenEvent;
    } else {
      this.dispatch({ eventName: 'bl-drawer-close', detailObj: { isActive: this.isActive } }) as BlocksCloseEvent;
    }
  }

  /**
   * @fires cancel If we call preventDefault on cancel event object to not close drawer.
   * @returns boolean
   */
  private emitCancelEvent(): boolean {
    const cancelEvent = this.dispatch({
      eventName: 'bl-drawer-cancel',
      detailObj: {},
      optionsObj: { cancelable: true }
    }) as BlocksCancelEvent;

    return cancelEvent.defaultPrevented;
  }

  /**
   * By default, a dialog invoked by the showModal() method can be dismissed by pressing the Esc key.
   * When we use disableEscClick we should prevent this behavior
   */
  private preventDrawerClose(event: Event): void {
    if (this.disableEscClick) {
      event.preventDefault();
      return;
    } else {
      this.toggle();
    }
  }

  /**
   * toggleWhenCollapsed drawer
   * @fires open - Fires when we open drawer by clicking hideButton icon
   * 1) Toggle the active state between true and false
   */
  private toggleWhenCollapsed() {
    this.isDrawerExpandedFromHideButton = true;
    this.toggle();
  }

  /**
   * Checks if screen size is below mobileBreakPoint property.
   */
  private setMobile = () => {
    const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (width < this.mobileBreakPoint) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }
  };

  /** Button template for Tab Right and Tab Left Drawer variants based on drawer state*/
  private buttonTemplate(): TemplateResult {
    return html`
      ${this.isButtonHidden
        ? html`
            <cat-button
              part="hide-button"
              exportparts=${this.getExportParts('button-hide', 'button')}
              variant="primary"
              ?hideText=${true}
              class="cat-c-drawer__hide-button ${this.hideButton === 'left' ? 'cat-c-drawer__hide-button--left' : 'cat-c-drawer__hide-button--right'}"
              .expanded=${this.isActive}
              @click=${!this.isActive ? this.toggleWhenCollapsed : this.toggle}
            >
              ${this.isActive ? this.hideButtonText : this.showButtonText}
              ${this.hideButton === 'left'
                ? html`
                    <cat-icon-arrow-triangular-left
                      part="button-after-icon"
                      exportparts=${this.getExportParts('arrow-triangular-left', 'icon')}
                      slot="after"
                    ></cat-icon-arrow-triangular-left>
                  `
                : html`
                    <cat-icon-arrow-triangular-right
                      part="button-after-icon"
                      exportparts=${this.getExportParts('arrow-triangular-right', 'icon')}
                      slot="after"
                    ></cat-icon-arrow-triangular-right>
                  `}
            </cat-button>
          `
        : nothing}
    `;
  }

  render(): TemplateResult {
    const isOrientationBottom = this.orientation == 'bottom' && this.isMobile;

    const componentClassName = this.componentClassNames('cat-c-drawer', {
      'cat-c-drawer--right': this.position === 'right',
      'cat-c-drawer--backdrop': this.hasBackdrop,
      'cat-c-drawer--center': this.footerAlignment === 'center',
      'cat-c-drawer--lg': this.size === 'lg',
      'cat-c-drawer--size-default': this.size === 'default',
      'cat-is-active': this.isActive,
      'cat-c-side-panel': this.isButtonHidden && !this.isActive,
      'cat-c-drawer--orientation-bottom': isOrientationBottom,
      'cat-c-drawer--vertical-height-default': isOrientationBottom && this.verticalMaxHeight == 'default',
      'cat-c-drawer--vertical-height-large': isOrientationBottom && this.verticalMaxHeight == 'large',
      'cat-c-drawer--has-header': this.hasHeaderSlot,
      'cat-c-drawer--has-actions': this.hasActionsSlot,
      'cat-c-drawer--has-footer': this.slots.hasSlotted('footer'),
      'cat-c-drawer--has-drawer-header-wrapper': this.hasHeaderSlot || this.hasActionsSlot || !this.hideCloseButton,
      'cat-c-drawer--has-default': this.preserveContent || this.isActive
    });
    const tag = this.isButtonHidden && !this.isActive ? literal`div` : literal`dialog`;
    return html`
      <div part="base" class="${componentClassName}" @click=${this.handleOnClickOutside}>
        <${tag}
          part="panel"
          @cancel=${this.preventDrawerClose}
          aria-labelledby=${ifDefined(this.isActive ? this.ariaLabelledBy : undefined)}
          class="cat-c-drawer__panel"
          aria-modal="true"
          tabindex="-1"
        >
          ${this.buttonTemplate()}
          <div class="cat-c-drawer__panel-wrapper" inert=${ifDefined(!this.isActive ? true : undefined)}>
            <div part="header-wrapper" class="cat-c-drawer__header">
              <div part="header-content" class="cat-c-drawer__header-content" id=${ifDefined(this.ariaLabelledBy)}>
                <slot part="part-header" name="header"></slot>
              </div>
              <slot part="part-actions" class="cat-c-drawer__header-actions" name="actions"></slot>
              ${
                this.hideCloseButton
                  ? nothing
                  : html`
                      <cat-button
                        part="close-button"
                        exportparts=${this.getExportParts('button-close', 'button')}
                        variant="ghost"
                        ?hideText=${true}
                        class="cat-c-drawer__close-button"
                        @click=${this.toggle}
                      >
                        ${this.closeButtonText}
                        <cat-icon-close
                          part="close-button-after-icon"
                          exportparts=${this.getExportParts('close', 'icon')}
                          slot="after"
                        ></cat-icon-close>
                      </cat-button>
                    `
              }
            </div>
            <div part="bottom-wrapper" class="cat-c-drawer--bottom-wrapper">
              <div part="body" class="cat-c-drawer__body" inert=${ifDefined(!this.isActive ? true : undefined)}>
                <slot part="part-default"></slot>
              </div>
              <footer part="footer-wrapper" class="cat-c-drawer__footer"><slot part="part-footer" name="footer"></slot></footer>
            </div>
          </div>
        </${tag}>
      </div>
    `;
  }
}

if (customElements.get('cat-drawer') === undefined) {
  customElements.define('cat-drawer', BlocksDrawer);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-drawer': BlocksDrawer;
  }
}
